import { theme } from '@arnold/common';
import styled from '@emotion/styled';

const pill = `
  display: flex;
  align-items: center;
  background: ${theme.colors.backgroundCover.default};
  border: 1px solid ${theme.colors.borderSeparator.default};
  border-radius: 10px;
  box-shadow: ${theme.shadows.elevationContentBlockOutstanding};
`;

/* ---- Block chrome ---- */
export const Block = styled.div<{ dragging?: boolean }>`
  position: relative;
  min-width: 0;
  height: 100%;
  border-radius: 8px;
  opacity: ${({ dragging }) => (dragging ? 0.4 : 1)};
  &:hover {
    z-index: 2;
    outline: 2px solid ${theme.colors.actionPrimary.default};
    outline-offset: 4px;
  }
  &:hover > .chrome {
    visibility: visible;
  }
`;
export const Handle = styled.button<{ outside?: boolean }>`
  ${pill}
  visibility: hidden;
  position: absolute;
  left: ${({ outside }) => (outside ? '-24px' : '-14px')};
  top: 50%;
  transform: translateY(-50%);
  width: ${({ outside }) => (outside ? '24px' : '28px')};
  height: 40px;
  padding: 0;
  justify-content: center;
  cursor: grab;
  color: ${theme.colors.text.secondary};
  svg {
    width: 14px;
    height: 14px;
  }
  svg path {
    fill: currentColor;
  }
  &:active {
    cursor: grabbing;
  }
`;
export const Toolbar = styled.div`
  ${pill}
  visibility: hidden;
  position: absolute;
  top: -18px;
  right: 12px;
  height: 36px;
  padding: 0 4px;
  gap: 2px;
`;
export const ToolButton = styled.button<{ danger?: boolean }>`
  display: grid;
  place-items: center;
  width: 30px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  svg {
    width: 18px;
    height: 18px;
  }
  svg path {
    fill: currentColor;
  }
  &:hover:not(:disabled) {
    background: ${theme.colors.backgroundBasic.active};
    color: ${({ danger }) => (danger ? theme.colors.emotionDanger.default : theme.colors.actionPrimary.default)};
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;
export const WidthButton = styled(ToolButton)<{ active?: boolean }>`
  width: auto;
  padding: 0 7px;
  color: ${({ active }) => (active ? theme.colors.actionPrimary.default : theme.colors.text.secondary)};
  background: ${({ active }) => (active ? theme.colors.backgroundBasic.active : 'transparent')};
`;
export const ToolSeparator = styled.span`
  width: 1px;
  height: 18px;
  margin: 0 3px;
  background: ${theme.colors.borderSeparator.default};
`;

/* ---- Insert zone ---- */
export const Zone = styled.div<{ always?: boolean; active?: boolean }>`
  grid-column: 1 / -1;
  position: relative;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: ${({ active }) => (active ? '3px' : '1px')};
    transform: translateY(-50%);
    border-radius: 2px;
    background: ${({ active }) => (active ? theme.colors.actionPrimary.default : theme.colors.borderSeparator.default)};
    opacity: ${({ active, always }) => (active || always ? 1 : 0)};
    transition: opacity 0.12s ease;
  }
  &:hover::before {
    opacity: 1;
  }
  > div {
    position: relative;
    display: flex;
    gap: 6px;
    padding: 0 6px;
    background: ${theme.colors.backgroundBasic.default};
    opacity: ${({ always }) => (always ? 1 : 0)};
    transition: opacity 0.12s ease;
  }
  &:hover > div {
    opacity: 1;
  }
`;
export const ZoneButton = styled.button`
  ${pill}
  gap: 4px;
  height: 30px;
  padding: 0 12px 0 8px;
  border-radius: 15px;
  box-shadow: none;
  color: ${theme.colors.text.primary};
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  svg {
    width: 16px;
    height: 16px;
  }
  svg path {
    fill: ${theme.colors.actionPrimary.default};
  }
  &:hover {
    border-color: ${theme.colors.actionPrimary.default};
    color: ${theme.colors.actionPrimary.default};
  }
`;
export const ZoneHint = styled.span`
  align-self: center;
  font-size: 12px;
  color: ${theme.colors.text.secondary};
  padding-right: 6px;
`;

/* ---- Inline text ---- */
export const InlineInput = styled('input', { shouldForwardProp: (prop) => prop !== 'variant' })<{
  variant: 'section' | 'table';
}>`
  display: block;
  width: 100%;
  min-width: 0;
  margin: 0 -6px;
  padding: 4px 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-family: 'Roboto';
  font-weight: 500;
  font-size: ${({ variant }) => (variant === 'section' ? '22px' : theme.typography.heading.small.default.fontSize)};
  line-height: 120%;
  color: ${theme.colors.text.primary};
  transition: background-color 0.12s ease;
  &::placeholder {
    color: ${theme.colors.text.disabled};
    font-weight: 400;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
  &:focus {
    outline: 0;
    background: ${theme.colors.backgroundCover.default};
    box-shadow: 0 0 0 1px ${theme.colors.borderSeparator.default};
  }
`;

/* ---- Dock ---- */
export const DockRoot = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  font-size: 13px;
  color: ${theme.colors.text.primary};
  /* The column is only a layout box; the report underneath stays scrollable and clickable next to the menu. */
  pointer-events: none;
  > * {
    pointer-events: auto;
  }
  &,
  * {
    box-sizing: border-box;
  }
  @media (max-width: 1023px) {
    display: none;
  }
`;
export const Fab = styled.button`
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${theme.colors.actionPrimary.default};
  color: ${theme.colors.textInverted.primary};
  box-shadow: ${theme.shadows.elevationContentBlockOutstanding};
  cursor: pointer;
  transition: transform 0.12s ease;
  svg {
    width: 24px;
    height: 24px;
  }
  svg path {
    fill: currentColor;
  }
  &:hover {
    transform: scale(1.06);
  }
`;
export const DockPill = styled.div`
  ${pill}
  gap: 8px;
  padding: 8px 8px 8px 14px;
  border-radius: 14px;
  max-width: min(720px, calc(100vw - 48px));
`;
export const DockStatus = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding-right: 6px;
  font-size: 12px;
  color: ${theme.colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const StatusDot = styled.span<{ warn?: boolean }>`
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ warn }) => (warn ? theme.colors.emotionWarning.default : theme.colors.emotionSuccess.default)};
`;
export const DockDivider = styled.span`
  width: 1px;
  height: 22px;
  background: ${theme.colors.borderSeparator.default};
`;
export const FloatingCard = styled.div`
  ${pill}
  flex-direction: column;
  align-items: stretch;
  width: 400px;
  max-height: min(520px, calc(100vh - 140px));
  border-radius: 14px;
  overflow: hidden;
`;
export const FloatingCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
  h3 {
    flex: 1;
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
`;
export const FloatingCardBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 16px 8px;
`;

/* ---- Library ---- */
export const LibraryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
  strong {
    display: block;
    font-weight: 500;
    overflow-wrap: anywhere;
  }
`;
export const StatusBadge = styled.span<{ tone?: 'positive' | 'neutral' | 'warn' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ tone }) =>
    tone === 'positive'
      ? theme.colors.emotionSuccess.default
      : tone === 'warn'
        ? theme.colors.emotionWarning.default
        : theme.colors.text.secondary};
  background: ${({ tone }) =>
    tone === 'positive'
      ? theme.colors.emotionSuccess.background
      : tone === 'warn'
        ? theme.colors.emotionWarning.background
        : theme.colors.backgroundBasic.active};
`;
export const Spacer = styled.span`
  flex: 1;
  min-width: 0;
`;
export const PresetBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: ${theme.colors.backgroundBasic.default};
`;
export const ErrorBubble = styled.div`
  max-width: 400px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${theme.colors.emotionDanger.background};
  color: ${theme.colors.emotionDanger.default};
  font-size: 12px;
`;
export const SectionTitle = styled.h3`
  margin: 18px 0 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.colors.text.secondary};
  &:first-of-type {
    margin-top: 0;
  }
`;
